import React from 'react';
import { useFormContext } from 'react-hook-form';
import { styled, Box, Button } from '@mui/material';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import EqualisationsAllocationsTable, {
  ColumnConfig,
} from 'components/EqualisationElements/EqualisationsAllocationsTable';
import NumericInputCell from 'components/EqualisationElements/NumericInputCell';
import CheckboxCell from 'components/EqualisationElements/CheckboxCell';
import ButtonGroup from 'components/ButtonGroup';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useParams } from 'react-router-dom';
import api from 'lib/trpcClient';

const StyledTableWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

const NetEqualisationInterestPaymentTable: React.FC<{
  netEqualisationInterestPaymentBreakdown: Array<{
    commitmentId: string;
    investorName: string;
    equalisationInterestTotalLcy: number;
    equalisationInterestPercentage: number;
    aggregateTotalLcy: number;
    excludeFromEqualisationInterest?: boolean;
  }>;
  baseCurrency: CurrencyCode;
  totalEqualisationInterestLcy: number;
}> = ({
  netEqualisationInterestPaymentBreakdown,
  baseCurrency,
  totalEqualisationInterestLcy,
}) => {
  const { control, reset, setValue } = useFormContext();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const [equalisationInterest] = api.equalisations.getInterest.useSuspenseQuery(
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );

  const columns: Array<ColumnConfig> = [
    {
      header: 'Name',
      width: '20%',
      renderCell: (row) => row.investorName,
    },
    {
      header: 'Aggregate equalisation payable / (receivable)',
      width: '15%',
      renderCell: (row) =>
        numberToCurrencyString(row.aggregateTotalLcy, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      header: 'Equalisation interest percentage',
      width: '15%',
      renderCell: (row) => `${row.equalisationInterestPercentage}%`,
    },
    {
      header: 'Equalisation interest payable / (receivable)',
      width: '15%',
      renderCell: (_row, rowIndex) => (
        <NumericInputCell
          fixedDecimalScale={true}
          control={control}
          allowNegative={true}
          name={`equalisationInterestPaymentBreakdown.${rowIndex}.equalisationInterestTotalLcy`}
          currency={baseCurrency}
          disabled={_row.excludeFromEqualisationInterest}
        />
      ),
      renderFooter: () =>
        numberToCurrencyString(totalEqualisationInterestLcy, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      header: 'Exclude from equalisation interest',
      width: '15%',
      renderCell: (_row, rowIndex) => (
        <CheckboxCell
          control={control}
          name={`equalisationInterestPaymentBreakdown.${rowIndex}.excludeFromEqualisationInterest`}
          onChange={(value) => {
            if (value) {
              setValue(
                `equalisationInterestPaymentBreakdown.${rowIndex}.equalisationInterestTotalLcy`,
                0,
              );
            }
          }}
        />
      ),
    },
  ];

  return (
    <StyledTableWrapper>
      <EqualisationsAllocationsTable
        columns={columns}
        rows={netEqualisationInterestPaymentBreakdown}
        showAllocationRule={false}
        isNotEditable={false}
      />
      <ButtonGroup align="right">
        <Button
          variant="outlined"
          onClick={() =>
            reset({
              equalisationInterestPaymentBreakdown:
                equalisationInterest.netEqualisationInterestPaymentBreakdown.map(
                  (item) => ({
                    ...item,
                    excludeFromEqualisationInterest: false,
                  }),
                ),
            })
          }
        >
          Reset interest totals
        </Button>
      </ButtonGroup>
    </StyledTableWrapper>
  );
};

export default NetEqualisationInterestPaymentTable;
